import { useState, useCallback } from 'react';
import { useSubmitHighscore } from '@/hooks/useHighscores';
import { GameScreen } from './GameScreen';
import { StartModal } from './StartModal';
import { EndModal } from './EndModal';
import { GameHUD } from './GameHUD';
import type { GameState } from './types';

const GAME_ID = 'cabinet-transformer';

const INITIAL_STATE: GameState = {
  status: 'idle',
  score: 0,
  timeRemaining: 30000,
  missedCabinets: 0,
  hitCabinets: 0,
  level: 1,
  autoAimActive: false,
  autoAimRemaining: 0,
};

export function CabinetTransformerGame() {
  const [gameState, setGameState] = useState<GameState>(INITIAL_STATE);
  const [showStartModal, setShowStartModal] = useState(true);
  const [showEndModal, setShowEndModal] = useState(false);
  const submitHighscore = useSubmitHighscore();

  const handleStart = useCallback(() => {
    setShowStartModal(false);
    setShowEndModal(false);
    setGameState({
      ...INITIAL_STATE,
      status: 'playing',
    });
  }, []);

  const handleGameEnd = useCallback((finalState: GameState) => {
    setGameState({ ...finalState, status: 'ended' });
    setShowEndModal(true);
  }, []);

  const handleScoreSubmit = useCallback(async (name: string, email?: string) => {
    await submitHighscore.mutateAsync({
      game_id: GAME_ID,
      player_name: name,
      email: email || undefined,
      score: gameState.score,
      level: gameState.level,
      metadata: {
        hitCabinets: gameState.hitCabinets,
        missedCabinets: gameState.missedCabinets,
      },
    });
  }, [gameState, submitHighscore]);

  const handlePlayAgain = useCallback(() => {
    setShowEndModal(false);
    setShowStartModal(true);
    setGameState(INITIAL_STATE);
  }, []);

  const handleStop = useCallback(() => {
    setGameState((prev) => ({ ...prev, status: 'ended' }));
    setShowEndModal(true);
  }, []);

  const updateGameState = useCallback((updates: Partial<GameState>) => {
    setGameState((prev) => ({ ...prev, ...updates }));
  }, []);

  return (
    <div className="min-h-screen bg-bg-deep flex items-center justify-center p-4">
      <div className="game-container">
        {/* Game HUD */}
        {gameState.status === 'playing' && (
          <GameHUD 
            gameState={gameState} 
            onStop={handleStop}
          />
        )}

        {/* Game Screen */}
        <GameScreen
          gameState={gameState}
          onGameEnd={handleGameEnd}
          updateGameState={updateGameState}
        />

        {/* Modals */}
        {showStartModal && (
          <StartModal onStart={handleStart} />
        )}

        {showEndModal && (
          <EndModal
            gameState={gameState}
            onSubmit={handleScoreSubmit}
            onPlayAgain={handlePlayAgain}
            isSubmitting={submitHighscore.isPending}
          />
        )}
      </div>
    </div>
  );
}
